import { Alert, Button, Space } from "antd"
import React, { FC, memo } from "react"
import { URL_FORGOT_PASSWORD, URL_LOGIN } from "../../models/url"
import { Link, useHistory } from "react-router-dom"
import { ApolloError } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { EndUserEmailRegistrationStatus } from "../../generated/graphql"

type LoginErrorProps = {
    title: string
    msg: string | React.ReactNode
}

export type ErrorType = { [key: string]: LoginErrorProps }

type RegistrationPanelProps = {
    errorType: keyof ErrorType
    createBookerError: ApolloError | undefined
    createAndRegisterError: ApolloError | undefined
    emailIsUnique: boolean
    // sc-3358: CONFIRMABLE = Kundendatensatz ohne eigenen Login → Bestätigungs-Flow
    emailStatus?: EndUserEmailRegistrationStatus
    confirmationMailState?: "idle" | "loading" | "sent"
    onRequestConfirmationMail?: () => void
}

export const RegistrationPanelErrors: FC<RegistrationPanelProps> = ({ createBookerError, createAndRegisterError, emailIsUnique, errorType, emailStatus, confirmationMailState = "idle", onRequestConfirmationMail }) => {
    const history = useHistory()
    const { t } = useTranslation()
    const renderIsNotUniqueMsg = () => {
        return (
            <Space direction='vertical' style={{ paddingTop: 20, paddingBottom: 20 }}>
                <p>
                    {t(
                        "registrationPanelErrors.emailIsNotUnique.msgPartOne",
                        "Falls du schon einmal einen Kurs bei uns gebucht hast, haben wir deine Daten bereits in unser neues Buchungssystem übernommen."
                    )}
                </p>
                <p>{t("registrationPanelErrors.emailIsNotUnique.msgPartTwo", "Du kannst dich hier anmelden")}</p>
                <Button type='primary' ghost block>
                    <Link to={{ pathname: URL_LOGIN, state: { referer: history.location.pathname } }}>{t("registrationPanelErrors.emailIsNotUnique.buttons.login", "Anmelden")}</Link>
                </Button>
                <p>{t("registrationPanelErrors.emailIsNotUnique.msgPartThree", "Falls du dein Passwort noch nicht weißt, oder vergessen hast, kannst du dir hier ein neues anlegen.")}</p>
                <div>
                    <Button type='primary' ghost block style={{ marginBottom: 20 }}>
                        <Link
                            to={{
                                pathname: URL_FORGOT_PASSWORD,
                                state: { referer: history.location.pathname },
                            }}
                        >
                            {t("registrationPanelErrors.emailIsNotUnique.buttons.requestPassword", "Passwort erhalten")}
                        </Link>
                    </Button>
                </div>
            </Space>
        )
    }

    const errors: ErrorType = {
        errorNotFound: {
            title: t("registrationPanelErrors.errorNotFound.title", "Unbekannter Fehler"),
            msg: t("registrationPanelErrors.errorNotFound.msg", "Es gab einen unbekannten Fehler beim Registrieren"),
        },
        userLoginError: {
            title: t("registrationPanelErrors.userLoginError.title", "Fehler beim Registrieren"),
            msg: t("registrationPanelErrors.userLoginError.msg", "Es tut uns leid, es gab einen Fehler beim Registrieren"),
        },
        wrongPassword: { title: t("registrationPanelErrors.wrongPassword.title", "Falsches Passwort"), msg: t("registrationPanelErrors.wrongPassword.msg", "Das eingegebene Passwort ist falsch.") },
        userNotFound: {
            title: t("registrationPanelErrors.userNotFound.title", "Benutzer nicht gefunden"),
            msg: t("registrationPanelErrors.userNotFound.msg", "Wir haben leider keine(n) Nutzer:in mit dieser E-Mail-Adresse gefunden."),
        },
        missingUserName: {
            title: t("registrationPanelErrors.missingUserName.title", "Benutzername fehlt"),
            msg: t("registrationPanelErrors.missingUserName.msg", "Bitte geben Sie Ihren Benutzernamen ein."),
        },
        missingPassword: { title: t("registrationPanelErrors.missingPassword.title", "Passwort fehlt"), msg: t("registrationPanelErrors.missingPassword.msg", "Bitte geben Sie Ihr Passwort ein.") },
        missingBoth: { title: t("registrationPanelErrors.missingBoth.title", "Keine Daten"), msg: t("registrationPanelErrors.missingBoth.msg", "Bitte gib Daten ein.") },
        emailIsNotUnique: {
            title: t("registrationPanelErrors.emailIsNotUnique.title", "E-Mail Adresse bereits vergeben"),
            msg: renderIsNotUniqueMsg(),
        },
    }

    const showErrorPanel = (errorType: keyof ErrorType) => {
        if (createBookerError) return <Alert style={{ textAlign: "center" }} type='error' message={errors[errorType].title} description={errors[errorType].msg} />
        if (createAndRegisterError) {
            return <Alert style={{ textAlign: "center" }} type='error' message={errors[errorType].title} description={errors[errorType].msg} />
        }

        // sc-3358: Booker ohne eigenen Login → statt Abweisung den Bestätigungs-Flow
        // anbieten (Info-Alert, kein Fehler — der Fall ist vollständig selbst lösbar).
        if (emailStatus === EndUserEmailRegistrationStatus.Confirmable) {
            if (confirmationMailState === "sent") {
                return (
                    <Alert
                        style={{ textAlign: "center" }}
                        type='success'
                        data-testid='registration-confirmable-sent'
                        message={t("registrationPanelErrors.confirmable.sentTitle", "E-Mail ist unterwegs!")}
                        description={t(
                            "registrationPanelErrors.confirmable.sentMsg",
                            "Bitte prüfe dein Postfach und klicke auf den Aktivierungslink, um ein Passwort festzulegen. Der Link ist 24 Stunden gültig."
                        )}
                    />
                )
            }

            return (
                <Alert
                    style={{ textAlign: "center" }}
                    type='info'
                    data-testid='registration-confirmable-hint'
                    message={t("registrationPanelErrors.confirmable.title", "Wir kennen dich schon 😊")}
                    description={
                        <Space direction='vertical' style={{ paddingTop: 12, paddingBottom: 12 }}>
                            <p>
                                {t(
                                    "registrationPanelErrors.confirmable.msg",
                                    "Zu deiner E-Mail-Adresse existiert bereits ein Kundendatensatz, aber noch kein Online-Zugang. Fordere eine Bestätigungs-E-Mail an — über den Link darin legst du dein Passwort fest und bist sofort angemeldet."
                                )}
                            </p>
                            <Button type='primary' block loading={confirmationMailState === "loading"} onClick={onRequestConfirmationMail} data-testid='registration-confirmable-request-button'>
                                {t("registrationPanelErrors.confirmable.requestButton", "Bestätigungs-E-Mail anfordern")}
                            </Button>
                        </Space>
                    }
                />
            )
        }

        // Mitarbeiter-Zugang ohne Kundenkonto: Login/Passwort-Reset würden in die Sackgasse
        // führen — nur der Admin kann den Bucher in der Verwaltung anlegen.
        if (emailStatus === EndUserEmailRegistrationStatus.TakenByStaff) {
            return (
                <Alert
                    style={{ textAlign: "center" }}
                    type='error'
                    data-testid='registration-taken-by-staff'
                    message={t("registrationPanelErrors.takenByStaff.title", "Registrierung mit dieser E-Mail-Adresse nicht möglich")}
                    description={t(
                        "registrationPanelErrors.takenByStaff.msg",
                        "Diese E-Mail-Adresse gehört bereits zu einem Mitarbeiter-Zugang. Ein Kundenkonto dazu kann nur der Anbieter in der Verwaltung anlegen. Bitte wende dich direkt an ihn."
                    )}
                />
            )
        }

        if (emailIsUnique === false) {
            return (
                <Alert
                    style={{ textAlign: "center" }}
                    type='error'
                    message={t("registrationPanelErrors.emailIsNotUnique.title", "Hey, diese Email-Adresse kennen wir bereits 😊")}
                    description={renderIsNotUniqueMsg()}
                />
            )
        }
    }

    return <>{showErrorPanel(errorType)}</>
}

export default memo(RegistrationPanelErrors)
